import React, { useState } from 'react';
import { X, MessageSquare, Clock } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { STATUS_OPTIONS, canSelectStatus, getStatusProgressionMessage } from '../../utils/jobCardStatus';

const JobCardStatusUpdate = ({ jobCard, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(jobCard.status);
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`/api/job-cards/${jobCard.id}/status`, {
        status,
        comments: comments.trim()
      });
      
      toast.success('Job card status updated successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Update Job Card Status</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600">Job Card</div>
            <div className="font-medium">{jobCard.orderNo}</div>
            <div className="text-sm text-gray-600 mt-1">
              {jobCard.customer?.name} - {jobCard.vehicle?.make} {jobCard.vehicle?.model}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Status
            </label>
            <div className="space-y-2">
              {STATUS_OPTIONS.map((option) => {
                const canSelect = canSelectStatus(option.value, jobCard.status, user?.role);
                
                return (
                  <label key={option.value} className={`flex items-center ${!canSelect ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="radio"
                      name="status"
                      value={option.value}
                      checked={status === option.value}
                      onChange={(e) => canSelect && setStatus(e.target.value)}
                      disabled={!canSelect}
                      className="mr-3"
                    />
                    <span className={`px-2 py-1 text-xs rounded-full ${option.bgColor}`}>
                      {option.label}
                    </span>
                    {!canSelect && option.value !== jobCard.status && (
                      <span className="ml-2 text-xs text-gray-400">(Cannot reverse)</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="mt-3 text-xs text-gray-500">
              <p>{getStatusProgressionMessage(user?.role)}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MessageSquare className="w-4 h-4 inline mr-1" />
              Comments/Notes
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={4}
              placeholder="Add your comments about the work progress, issues, or completion notes..."
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobCardStatusUpdate;